import Loading from '../../../Loading/Loading';
import Show from '../../../Show/Show';
import { AnswerSelectPresentationProps } from '../config';
import { AppointmentCard } from '../../../Assistance/AppointmentCard/AppointmentCard';
import { COLORS } from '../../../../config/designSystem/Colors';
import { Caption2Bold } from '../../../../config/designSystem/typography';
import { Countdown } from '../../../Countdown/Countdown';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { SPACING } from '../../../../config/designSystem/Spacing';
import { ScrollView, StyleSheet, View } from 'react-native';
import { getAnswerByKey, unwrapMeta } from '../../utils';
import { observer } from 'mobx-react';
import { useStores } from '../../../../hooks/useStores';

// show countdown warning when there are only X seconds remaining
const countdownWarningThreshold = 30;

const _PresentConfirmSession: FC<AnswerSelectPresentationProps> = ({
  answers,
  questionId,
  onNext
}) => {

  const { question: qstore, user, treeNav, language } = useStores();
  const labels = language?.translate.MOBILE?.ASSISTANCE?.APPOINTMENT;

  const confirm = getAnswerByKey('confirm', answers || []);
  const {
    title: counsellorName,
    picture,
    date,
    lockStartedAt = (new Date().getTime() / 1000).toString(),
    lockMinuteDuration = `${5}`,
    timeZone,
    assistanceModeLabel,
  } = unwrapMeta(['title', 'picture', 'date', 'fromTime', 'toTime', 'lockStartedAt', 'lockMinuteDuration', 'timeZone', 'assistanceModeLabel'], confirm);
  const countdownTo = Number(lockStartedAt) + Number(lockMinuteDuration) * 60 ;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [showCountdown, setShowCountDown] = useState(false);

  useEffect(() => {
    const now = Date.now() / 1000;
    const countdownDuration = countdownTo - now;
    if (countdownDuration > countdownWarningThreshold) {
      const countdownWarnAfter = countdownDuration - countdownWarningThreshold;
      console.log(new Date().toISOString(), 'Counsellor lock: will warn about timeout after ', countdownWarnAfter, ' seconds');
      timer.current = setTimeout(() => {
        setShowCountDown(true);
      }, countdownWarnAfter * 1000);
    } else {
      setShowCountDown(true);
    }
    return () => {
      clearTimeout(timer.current);
      setShowCountDown(false);
    };
  }, [countdownTo]);

  const onTimeout = useCallback(() => {
    console.log('Counsellor lock timeout. Go back to previous screen.');
    treeNav.moveBack();
  }, [treeNav]);

  return (
    <ScrollView
      contentContainerStyle={styles.scrollViewContainer}
    >
      <Show when={qstore.isReady(questionId)} fallback={<Loading />}>
        <Show when={confirm} keyed>
          {confirm => (
            <AppointmentCard
              appointmentModeLabelKey={assistanceModeLabel as string}
              counsellorName={counsellorName as string}
              counsellorPicture={picture as string}
              locale={user.user?.language || 'en'}
              date={date as string}
              timeZone={timeZone as string}
              onPress={onNext}
            />
          )}
        </Show>
        <Show when={showCountdown}>
          <View style={{justifyContent: 'center', width: '100%'}}>
            <Countdown
              to={countdownTo}
              onTimeout={onTimeout}
            >
              {({timeRemainingSeconds}) => (
                <Caption2Bold
                  color={COLORS.content.secondary}
                >
                  {labels?.LOCK_TIMEOUT?.replaceAll('{{timeRemainingSeconds}}', `${timeRemainingSeconds}`)}
                </Caption2Bold>
              )}
            </Countdown>
          </View>
        </Show>
      </Show>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollViewContainer: {
    display: 'flex',
    paddingVertical: SPACING['sp-8'],
    flexDirection: 'column',
    gap: SPACING['sp-16'],
    marginBottom: SPACING['sp-32']
  }
});

export const PresentConfirmSession = observer(_PresentConfirmSession);
